import { useState } from 'react'
import { Alert, Card, Col, DatePicker, Descriptions, Row, Space, Statistic, Tooltip, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  downloadFinanceExport,
  fetchBalanceSheet,
  type BalanceLine,
  type BalanceSheet,
} from '@/features/finance/financeApi'
import { CostCenterFilter } from '@/features/finance/CostCenterFilter'
import { OpenPeriodsAlert } from '@/features/finance/OpenPeriodsAlert'
import { ReportScopeNoteBar } from '@/features/finance/ReportScopeNoteBar'
import { StatementSection, type StatementRow } from '@/features/finance/StatementSection'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { seededName } from '@/shared/lib/seededName'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const ISO_DAY = 'YYYY-MM-DD'

export function FinanceBalanceSheetPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const [asOfDay, setAsOfDay] = useState<Dayjs>(tenantToday)
  const [nodeId, setNodeId] = useState<string | null>(null)
  const asOf = asOfDay.format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.finance.balanceSheet(asOf, nodeId),
    queryFn: ({ signal }) => fetchBalanceSheet({ asOf, nodeId }, signal),
  })

  const data = report.data
  const currency = data?.note.currency ?? ''

  const accountRows = (lines: readonly BalanceLine[]): StatementRow[] =>
    lines.map((line) => ({
      key: line.accountId,
      code: line.code,
      label: seededName('accounts', line.code, line.name, t),
      amountMinor: line.amountMinor,
    }))

  function interofficeRow(sheet: BalanceSheet, side: 'asset' | 'liability'): StatementRow[] {
    if (!sheet.interoffice) return []
    const { postedMinor, derivedMinor, totalMinor } = sheet.interoffice
    const owed = totalMinor < 0
    if (owed !== (side === 'liability')) return []
    return [
      {
        key: 'interoffice',
        code: null,
        emphasis: true,
        amountMinor: owed ? -totalMinor : totalMinor,
        label: (
          <Space size={4}>
            {t(owed ? 'finance.balanceSheet.interofficeDebt' : 'finance.balanceSheet.interoffice')}
            <Tooltip
              title={t('finance.balanceSheet.interofficeHint', {
                posted: formatMoneyValue(money(postedMinor, currency)),
                derived: formatMoneyValue(money(derivedMinor, currency)),
              })}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        ),
      },
    ]
  }

  function stakeholderDebtRow(sheet: BalanceSheet): StatementRow[] {
    if (sheet.stakeholderDebtMinor === 0) return []
    return [
      {
        key: 'stakeholder-debt',
        code: null,
        emphasis: true,
        amountMinor: sheet.stakeholderDebtMinor,
        label: (
          <Space size={4}>
            {t('finance.balanceSheet.stakeholderDebt')}
            <Tooltip title={t('finance.balanceSheet.stakeholderDebtHint')}>
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        ),
      },
    ]
  }

  const assetRows = data
    ? [...accountRows(data.assets), ...stakeholderDebtRow(data), ...interofficeRow(data, 'asset')]
    : []
  const liabilityRows = data ? [...accountRows(data.liabilities), ...interofficeRow(data, 'liability')] : []
  const equityRows: StatementRow[] = data
    ? [
        ...accountRows(data.equity),
        {
          key: 'currentEarnings',
          code: null,
          label: t('finance.balanceSheet.currentEarnings'),
          amountMinor: data.currentEarningsMinor,
          emphasis: true,
        },
      ]
    : []

  return (
    <>
      <PageHeader
        title={t('finance.balanceSheet.title')}
        description={t('finance.balanceSheet.intro')}
        extra={
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('finance.balanceSheet.title')}
              disabled={report.isError}
              run={(format, onProgress) =>
                downloadFinanceExport('balance-sheet', format, { asOf, nodeId, onProgress })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('finance.balanceSheet.asOfLabel')}:</Typography.Text>
          <DatePicker
            value={asOfDay}
            onChange={(value) => {
              if (value) setAsOfDay(value)
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
          <CostCenterFilter value={nodeId} onChange={setNodeId} />
        </Space>
        <ReportScopeNoteBar note={data?.note} />
      </Card>

      <OpenPeriodsAlert note={data?.note} />

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.balanceSheet.totalAssets')}
                  value={formatMoneyValue(money(data?.totalAssetsMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.balanceSheet.totalLiabilities')}
                  value={formatMoneyValue(money(data?.totalLiabilitiesMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.balanceSheet.totalEquity')}
                  value={formatMoneyValue(money(data?.totalEquityMinor ?? 0, currency))}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('finance.balanceSheet.check')}
                  valueStyle={data && !data.balanced ? { color: '#cf1322' } : undefined}
                  value={
                    data
                      ? data.balanced
                        ? t('finance.balanceSheet.balancedYes')
                        : t('finance.balanceSheet.balancedNo')
                      : '—'
                  }
                />
              </Card>
            </Col>
          </Row>

          {data && !data.balanced ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('finance.balanceSheet.unbalanced')}
              description={t('finance.balanceSheet.unbalancedHint')}
            />
          ) : null}

          <StatementSection
            title={t('finance.balanceSheet.assets')}
            rows={assetRows}
            totalLabel={t('finance.balanceSheet.totalAssets')}
            totalMinor={data?.totalAssetsMinor}
            currency={currency}
            loading={report.isPending}
            emptyText={t('finance.balanceSheet.emptySection')}
          />
          <StatementSection
            title={t('finance.balanceSheet.liabilities')}
            rows={liabilityRows}
            totalLabel={t('finance.balanceSheet.totalLiabilities')}
            totalMinor={data?.totalLiabilitiesMinor}
            currency={currency}
            loading={report.isPending}
            emptyText={t('finance.balanceSheet.emptySection')}
          />
          <StatementSection
            title={t('finance.balanceSheet.equity')}
            rows={equityRows}
            totalLabel={t('finance.balanceSheet.totalEquity')}
            totalMinor={data?.totalEquityMinor}
            currency={currency}
            loading={report.isPending}
            emptyText={t('finance.balanceSheet.emptySection')}
          />

          {data?.eliminations ? (
            <Card size="small" title={t('finance.balanceSheet.eliminations.title')}>
              <Typography.Paragraph type="secondary">
                {t('finance.balanceSheet.eliminations.hint')}
              </Typography.Paragraph>
              <Descriptions column={{ xs: 1, md: 2 }} size="small">
                <Descriptions.Item label={t('finance.balanceSheet.eliminations.interoffice')}>
                  {formatMoneyValue(money(data.eliminations.interofficeMinor, currency))}
                </Descriptions.Item>
                <Descriptions.Item label={t('finance.balanceSheet.eliminations.markup')}>
                  {formatMoneyValue(money(data.eliminations.markupMinor, currency))}
                </Descriptions.Item>
              </Descriptions>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {data.eliminations.markupAsOf
                  ? t('finance.balanceSheet.eliminations.markupAsOf', {
                      date: data.eliminations.markupAsOf,
                    })
                  : t('finance.balanceSheet.eliminations.noMarkup')}
              </Typography.Text>
            </Card>
          ) : null}
        </>
      )}
    </>
  )
}
